/**
 * Global state store using Zustand
 */

import { create } from 'zustand'
import type { User, Task, DocTreeResponse, MarkdownTasksResponse, TaskStatusOverride } from './api'
import * as api from './api'

interface AppState {
    // Auth
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean

    // Docs
    docTree: DocTreeResponse | null

    // Tasks
    tasks: Task[]

    // Markdown Tasks
    markdownTasks: MarkdownTasksResponse | null
    taskStatuses: Record<string, TaskStatusOverride>

    // Actions
    checkAuth: () => Promise<void>
    login: () => void
    logout: () => Promise<void>
    fetchDocTree: () => Promise<void>
    fetchTasks: () => Promise<void>
    fetchMarkdownTasks: () => Promise<void>
    fetchTaskStatuses: () => Promise<void>
    updateMarkdownTaskStatus: (taskId: string, updates: { status?: 'todo' | 'in_progress' | 'done'; assignee?: string }) => Promise<void>
    createTask: (task: Partial<Task>) => Promise<Task>
    updateTask: (id: string, updates: Partial<Task>) => Promise<void>
    deleteTask: (id: string) => Promise<void>
}

export const useAppStore = create<AppState>((set, get) => ({
    // Initial state
    user: null,
    isAuthenticated: false,
    isLoading: true,
    docTree: null,
    tasks: [],
    markdownTasks: null,
    taskStatuses: {},

    // Auth actions
    checkAuth: async () => {
        try {
            const { authenticated, user } = await api.getSession()
            set({ user: user || null, isAuthenticated: authenticated, isLoading: false })
        } catch {
            set({ user: null, isAuthenticated: false, isLoading: false })
        }
    },

    login: () => {
        api.login()
    },

    logout: async () => {
        await api.logout()
        set({ user: null, isAuthenticated: false })
    },

    // Docs actions
    fetchDocTree: async () => {
        try {
            const docTree = await api.getDocTree()
            set({ docTree })
        } catch (error) {
            console.error('Failed to fetch doc tree:', error)
        }
    },

    // Task actions
    fetchTasks: async () => {
        try {
            const tasks = await api.getTasks()
            set({ tasks })
        } catch (error) {
            console.error('Failed to fetch tasks:', error)
        }
    },

    fetchMarkdownTasks: async () => {
        try {
            const [markdownTasks, { statuses }] = await Promise.all([
                api.getMarkdownTasks(),
                api.getTaskStatuses(),
            ])
            set({ markdownTasks, taskStatuses: statuses })
        } catch (error) {
            console.error('Failed to fetch markdown tasks:', error)
        }
    },

    fetchTaskStatuses: async () => {
        try {
            const { statuses } = await api.getTaskStatuses()
            set({ taskStatuses: statuses })
        } catch (error) {
            console.error('Failed to fetch task statuses:', error)
        }
    },

    updateMarkdownTaskStatus: async (taskId, updates) => {
        try {
            const { status } = await api.updateTaskStatus(taskId, updates)
            set({
                taskStatuses: { ...get().taskStatuses, [taskId]: status }
            })
        } catch (error) {
            console.error('Failed to update task status:', error)
            throw error
        }
    },

    createTask: async (task) => {
        const newTask = await api.createTask(task)
        set({ tasks: [...get().tasks, newTask] })
        return newTask
    },

    updateTask: async (id, updates) => {
        const updatedTask = await api.updateTask(id, updates)
        set({
            tasks: get().tasks.map(t => t.id === id ? updatedTask : t)
        })
    },

    deleteTask: async (id) => {
        await api.deleteTask(id)
        set({ tasks: get().tasks.filter(t => t.id !== id) })
    },
}))
